// Iter · Feb 2026 · R261 — Advanced Timesheet Report page.
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useUserPreferences } from "@/lib/userPrefs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import MultiSelect from "@/components/ui/multi-select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell, LineChart, Line, CartesianGrid, Sankey, Layer, Rectangle,
} from "recharts";
import {
  ChevronDown, ChevronRight, Download, Save, Trash2, GripVertical, X,
  Loader2, TrendingUp, TrendingDown, AlertTriangle, Filter,
  Maximize2, Minimize2, Globe, MessageSquarePlus,
  ArrowUp, ArrowDown, ArrowUpDown,
} from "lucide-react";
// R268bbb · Feb 2026 — Link the pivot grid label clicks to the app's
// existing popups so users can drill into a task/project/assignee
// without leaving the report.
import { TaskDetail } from "@/pages/app/Tasks";
import ProjectTasksPopup from "@/components/Client/ProjectTasksPopup";
import EmployeeProfileDialog from "@/components/employee/EmployeeProfileDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ProjectStatusUpdatesTab } from "@/components/project/ProjectSubTabs";

const PIVOT_OPTIONS = [
  { value: "client", label: "Client" },
  { value: "brand", label: "Brand" },
  { value: "project", label: "Project" },
  { value: "package", label: "Package" },
  { value: "assignee", label: "Assignee" },
  { value: "task", label: "Task" },
  { value: "category", label: "Category" },
  { value: "billable", label: "Billable" },
  { value: "department", label: "Department" },
];
const BUCKET_OPTIONS = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "quarter", label: "Quarter" },
  { value: "year", label: "Year" },
  { value: "none", label: "Total only" },
];
const PRESET_OPTIONS = [
  { value: "last7", label: "Last 7 days" },
  { value: "last30", label: "Last 30 days (rolling)" },
  { value: "this_month", label: "This month" },
  { value: "last_month", label: "Last month" },
  { value: "this_quarter", label: "This quarter" },
  { value: "this_year", label: "This year" },
  { value: "lifetime", label: "Lifetime" },
  { value: "custom", label: "Custom range…" },
];
const CHART_COLORS = ["#4F46E5", "#059669", "#DC2626", "#D97706", "#7C3AED", "#0891B2", "#DB2777", "#65A30D"];

function fmtH(h) {
  const n = Number(h) || 0;
  return n.toFixed(1) + "h";
}

// R268vv · Feb 2026 — Collapsible section with cross-device persistence.
// Uses user_preferences via useUserPreferences() so the open/closed
// state follows the user across browsers and devices. Storage path:
// `timesheet_adv.blocks.<blockKey> = "open" | "closed"`.
function CollapsibleBlock({ blockKey, title, subtitle, actions, defaultOpen = true, children }) {
  const { getPref, setPref } = useUserPreferences();
  const storedRaw = getPref(`timesheet_adv.blocks.${blockKey}`, null);
  const stored = storedRaw === "open" || storedRaw === "closed" ? storedRaw : null;
  const [open, setOpen] = useState(stored ? stored === "open" : defaultOpen);
  useEffect(() => {
    if (stored !== null) setOpen(stored === "open");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stored]);
  const toggle = () => {
    const next = !open;
    setOpen(next);
    setPref({ timesheet_adv: { blocks: { [blockKey]: next ? "open" : "closed" } } });
  };
  return (
    <div className="border border-border" data-testid={`ts-block-${blockKey}`}>
      <div className="flex items-center gap-2 px-3 py-1.5 bg-muted/40 border-b border-border cursor-pointer hover:bg-muted/60"
           onClick={toggle}
           role="button"
           data-testid={`ts-block-toggle-${blockKey}`}>
        {open ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{title}</span>
        {subtitle && <span className="text-[10px] text-muted-foreground">{subtitle}</span>}
        {actions && (
          <div className="ml-auto flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            {actions}
          </div>
        )}
      </div>
      {open && <div className="p-2">{children}</div>}
    </div>
  );
}

export default function ReportsTimesheetAdvanced() {
  const auth = useAuth();
  const wsId = auth && auth.activeWorkspaceId;

  const [preset, setPreset] = useState("last30");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [pivots, setPivots] = useState(["assignee", "project"]);
  const [bucket, setBucket] = useState("day");
  const [brandF, setBrandF] = useState([]);
  const [clientF, setClientF] = useState([]);
  const [projectF, setProjectF] = useState([]);
  const [assigneeF, setAssigneeF] = useState([]);
  const [billableF, setBillableF] = useState("all");
  const [search, setSearch] = useState("");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  // R268zz · Feb 2026 — Compare-period toggle. When ON we render two
  // extra columns (Prev H, Δ %) on every row of the pivot grid so
  // week-on-week under-utilisation jumps out per assignee/project.
  const [compareMode, setCompareMode] = useState(false);

  // R268bbb · Feb 2026 — Drill-in popups. Clicking a row label opens
  // the app's existing per-entity dialog:
  //   • Task     → TaskDetail (Edit Task popup from Tasks.jsx)
  //   • Project  → ProjectTasksPopup (existing project-tasks drill)
  //   • Assignee → RemarksDialog (Now-dashboard employee popup with subtabs)
  // A tiny MessageSquarePlus button before each project label opens
  // PostProjectUpdateDialog (project status update).
  const [openTask, setOpenTask] = useState(null);
  const [openProject, setOpenProject] = useState(null);
  const [openAssignee, setOpenAssignee] = useState(null);
  const [openProjectStatus, setOpenProjectStatus] = useState(null);
  // R268ccc · Feb 2026 — Row-selection highlight. Click a row to
  // "pin" it (persistent highlight while inspecting), click the same
  // row again to un-pin, or click any other row to swap the selection.
  // Independent of expand/collapse state.
  const [selectedRowId, setSelectedRowId] = useState(null);
  const toggleRowSelection = useCallback((id) => {
    setSelectedRowId((prev) => (prev === id ? null : id));
  }, []);

  const openTaskById = useCallback(async (taskId) => {
    if (!taskId) return;
    try {
      const { data } = await api.get(`/tasks/${taskId}`);
      setOpenTask(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Task not found")); }
  }, []);
  const [fullscreen, setFullscreen] = useState(false);
  const [brands, setBrands] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [members, setMembers] = useState([]);
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState("");
  // R268m · Feb 2026 — share presets with the whole workspace
  const [presetShared, setPresetShared] = useState(false);

  // R268bbb · Feb 2026 — Openers moved AFTER `projects` / `members`
  // declarations to avoid a temporal-dead-zone ReferenceError when the
  // callbacks close over those states.
  const openProjectById = useCallback((projectId) => {
    if (!projectId) return;
    const p = (projects || []).find((x) => x.id === projectId);
    if (p) setOpenProject(p);
    else toast.error("Project not found");
  }, [projects]);
  const openAssigneeById = useCallback((userId) => {
    if (!userId) return;
    const m = (members || []).find((x) => x.user_id === userId || x.id === userId);
    const name = (m && (m.user?.name || m.name || m.email)) || "Assignee";
    setOpenAssignee({ user_id: userId, name });
  }, [members]);
  const openProjectStatusById = useCallback((projectId) => {
    if (!projectId) return;
    const p = (projects || []).find((x) => x.id === projectId);
    if (p) setOpenProjectStatus(p);
    else toast.error("Project not found");
  }, [projects]);

  useEffect(() => {
    if (!wsId) return;
    api.get("/workspaces/" + wsId + "/brands").then((r) => setBrands(r.data || [])).catch(() => {});
    api.get("/workspaces/" + wsId + "/clients").then((r) => setClients((r.data && r.data.rows) || r.data || [])).catch(() => {});
    api.get("/workspaces/" + wsId + "/projects").then((r) => setProjects((r.data && r.data.rows) || r.data || [])).catch(() => {});
    api.get("/workspaces/" + wsId + "/members").then((r) => setMembers(r.data || [])).catch(() => {});
    api.get("/workspaces/" + wsId + "/reports/timesheet-advanced/presets").then((r) => setPresets(r.data || [])).catch(() => {});
  }, [wsId]);

  const buildReq = useCallback(function () {
    return {
      preset: preset === "custom" ? null : preset,
      start_date: preset === "custom" ? startDate : null,
      end_date: preset === "custom" ? endDate : null,
      pivots: pivots,
      bucket: bucket,
      brand_ids: brandF,
      client_ids: clientF,
      project_ids: projectF,
      assignee_ids: assigneeF,
      billable: billableF === "all" ? null : billableF,
      search: search || null,
      include_raw: false,
    };
  }, [preset, startDate, endDate, pivots, bucket, brandF, clientF, projectF, assigneeF, billableF, search]);

  const run = useCallback(async function () {
    if (!wsId) return;
    setLoading(true);
    try {
      const req = buildReq();
      const r = await api.post("/workspaces/" + wsId + "/reports/timesheet-advanced", req);
      // R268yy · Feb 2026 — Deep-clone the response so no downstream
      // code (React StrictMode double-invocation, third-party effect
      // hooks, sibling components) can mutate the same tree instance
      // between renders. This was the smoking gun behind "duplicate
      // — No project — rows grow on every expand/collapse click":
      // one of React's dev-mode double-mounts was appending to
      // r.data.tree.children[*].children via a shared reference.
      const cloned = JSON.parse(JSON.stringify(r.data));
      setData(cloned);
      appliedReqRef.current = req;
      // R268rr · Feb 2026 — Preserve the user's expand state across
      // re-runs. Only fall back to "top-level open" when the current
      // expanded set has no overlap with the new tree ids (e.g. after
      // a pivots-order change that fundamentally restructures the
      // hierarchy). This keeps the drill-down open while users tweak
      // date-range chips, search text, or billable filters.
      const kids = (cloned && cloned.tree && cloned.tree.children) || [];
      const allIds = new Set();
      const _walk = (n) => { (n.children || []).forEach((c) => { allIds.add(c.id); _walk(c); }); };
      _walk(cloned && cloned.tree ? cloned.tree : { children: [] });
      setExpanded((prev) => {
        const keep = new Set();
        prev.forEach((id) => { if (allIds.has(id)) keep.add(id); });
        if (keep.size === 0) {
          kids.forEach((c) => keep.add(c.id));
        }
        return keep;
      });
    } catch (e) {
      toast.error(apiErrorMessage(e, "Report failed"));
    } finally {
      setLoading(false);
    }
  }, [wsId, buildReq]);

  // R268vv · Feb 2026 — Explicit Apply model. Auto-run caused subtle
  // races where a chevron click's expanded-state update was clobbered
  // by an in-flight re-run (from a stale buildReq closure). Now the
  // report only fetches on:
  //   • initial mount (once we have wsId)
  //   • the user pressing the Apply/Run button (see `run` in JSX)
  //   • a preset being loaded (calls run() itself)
  // Filter/pivot changes are staged in state and highlighted as a
  // "pending" pill next to the Apply button — no automatic fetch.
  const didInitialRun = useRef(false);
  useEffect(function () {
    if (!wsId || didInitialRun.current) return;
    didInitialRun.current = true;
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsId]);

  // Track whether the last-applied config matches the current form
  // state so the UI can show a "changes pending" badge on Apply.
  const appliedReqRef = useRef(null);
  const dirty = useMemo(function () {
    if (!appliedReqRef.current) return false;
    try { return JSON.stringify(appliedReqRef.current) !== JSON.stringify(buildReq()); }
    catch { return false; }
  }, [buildReq]);

  const exportXlsx = async function () {
    try {
      const r = await api.post(
        "/workspaces/" + wsId + "/reports/timesheet-advanced/export.xlsx",
        buildReq(), { responseType: "blob" }
      );
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "timesheet-" + (data && data.range && data.range.start || "report") + ".xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Export failed"));
    }
  };

  const savePreset = async function () {
    if (!presetName.trim()) { toast.error("Give the preset a name first"); return; }
    try {
      const r = await api.post(
        "/workspaces/" + wsId + "/reports/timesheet-advanced/presets",
        { name: presetName.trim(), config: buildReq(), shared: presetShared }
      );
      setPresets(function (p) { return [r.data].concat(p); });
      setPresetName("");
      setPresetShared(false);
      toast.success(presetShared ? "Saved & shared with workspace" : "Saved preset");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };

  const loadPreset = function (p) {
    const c = p.config || {};
    setPreset(c.preset || (c.start_date ? "custom" : "last30"));
    setStartDate(c.start_date || "");
    setEndDate(c.end_date || "");
    setPivots(c.pivots || ["assignee"]);
    setBucket(c.bucket || "day");
    setBrandF(c.brand_ids || []);
    setClientF(c.client_ids || []);
    setProjectF(c.project_ids || []);
    setAssigneeF(c.assignee_ids || []);
    setBillableF(c.billable || "all");
    setSearch(c.search || "");
    // R268vv · Feb 2026 — Fire an immediate run so users don't need to
    // hit Apply after loading a preset. Waits a tick so state
    // batches flush before buildReq() is called inside run().
    setTimeout(function () { run(); }, 0);
  };

  const deletePreset = async function (p) {
    if (!window.confirm("Delete preset \"" + p.name + "\"?")) return;
    try {
      await api.delete("/workspaces/" + wsId + "/reports/timesheet-advanced/presets/" + p.id);
      setPresets(function (prev) { return prev.filter(function (x) { return x.id !== p.id; }); });
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const movePivot = function (idx, dir) {
    setPivots(function (prev) {
      const next = prev.slice();
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      const tmp = next[idx]; next[idx] = next[j]; next[j] = tmp;
      return next;
    });
  };
  const addPivot = function (v) {
    setPivots(function (prev) {
      if (prev.length >= 4 || prev.indexOf(v) >= 0) return prev;
      return prev.concat([v]);
    });
  };
  const dropPivot = function (idx) {
    setPivots(function (prev) { return prev.filter(function (_, i) { return i !== idx; }); });
  };
  // R268vv · Feb 2026 — HTML5 drag-and-drop reorder for pivot chips.
  // Keeps arrow buttons for accessibility. Uses a ref for the "grabbed"
  // index so we don't trigger a full re-render on every dragover.
  const dragFromRef = useRef(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const onPivotDragStart = function (idx) {
    dragFromRef.current = idx;
  };
  const onPivotDragOver = function (idx, e) {
    if (e && e.preventDefault) e.preventDefault();
    if (dragFromRef.current === null || dragFromRef.current === idx) return;
    setDragOverIdx(idx);
  };
  const onPivotDrop = function (idx) {
    const from = dragFromRef.current;
    dragFromRef.current = null;
    setDragOverIdx(null);
    if (from === null || from === idx) return;
    setPivots(function (prev) {
      const next = prev.slice();
      const [moved] = next.splice(from, 1);
      next.splice(idx, 0, moved);
      return next;
    });
  };
  const onPivotDragEnd = function () {
    dragFromRef.current = null;
    setDragOverIdx(null);
  };
  const toggleNode = function (id) {
    setExpanded(function (s) {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const collectAllIds = function (node, out) {
    (node.children || []).forEach(function (c) {
      if ((c.children || []).length > 0) {
        out.push(c.id);
        collectAllIds(c, out);
      }
    });
    return out;
  };
  const expandAll = function () {
    if (!data) return;
    const ids = collectAllIds(data.tree, []);
    setExpanded(new Set(ids));
  };
  const collapseAll = function () { setExpanded(new Set()); };

  const bucketCols = (data && data.bucket_cols) || [];
  const gridCols = "280px 90px 90px 90px 70px " + bucketCols.map(function () { return "70px"; }).join(" ");
  const minGridWidth = 880 + bucketCols.length * 70;

  return (
    <div className="p-4 space-y-3" data-testid="reports-timesheet-adv">
      <div className="flex items-baseline gap-3">
        <h1 className="text-2xl font-bold tracking-tight">Timesheet report</h1>
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Advanced · pivot</span>
        {data && data.scope === "my_projects" && (
          <Badge variant="outline" className="rounded-none text-[10px]">Scoped to my projects</Badge>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" className="rounded-none h-8"
                  onClick={exportXlsx} disabled={loading || !data}
                  data-testid="ts-export-xlsx">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Excel
          </Button>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="rounded-none h-8" data-testid="ts-presets-btn">
                <Save className="w-3.5 h-3.5 mr-1.5" /> Presets
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 rounded-none p-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">Saved presets</div>
              {presets.length === 0 && <div className="text-xs text-muted-foreground">No saved presets yet.</div>}
              <div className="space-y-1 max-h-56 overflow-auto mb-2">
                {presets.map(function (p) {
                  const isShared = !!p.shared;
                  return (
                    <div key={p.id} className="flex items-center gap-1 text-xs" data-testid={"ts-preset-" + p.id}>
                      {isShared && (
                        <Globe className="w-3 h-3 text-emerald-600 shrink-0"
                                  title={"Shared with workspace by " + (p.created_by_name || "someone")} />
                      )}
                      <button className="flex-1 text-left hover:text-primary truncate"
                              onClick={function () { loadPreset(p); }}>{p.name}</button>
                      <button onClick={function () { deletePreset(p); }} className="text-muted-foreground hover:text-rose-600"
                              data-testid={"ts-preset-del-" + p.id}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
              <div className="pt-2 border-t border-border space-y-1.5">
                <div className="flex gap-1">
                  <Input value={presetName} onChange={function (e) { setPresetName(e.target.value); }}
                          placeholder="Preset name…" className="rounded-none h-7 text-xs"
                          data-testid="ts-preset-name" />
                  <Button size="sm" className="rounded-none h-7 text-xs" onClick={savePreset} data-testid="ts-preset-save">
                    Save {presetShared ? "& share" : "current"}
                  </Button>
                </div>
                <label className="flex items-center gap-1.5 text-[11px] cursor-pointer text-muted-foreground hover:text-foreground">
                  <input type="checkbox"
                            checked={presetShared}
                            onChange={function (e) { setPresetShared(e.target.checked); }}
                            data-testid="ts-preset-shared" />
                  <Globe className="w-3 h-3" />
                  Share with everyone in this workspace
                </label>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="border border-border p-3 space-y-2 bg-muted/10" data-testid="ts-filter-bar">
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={preset} onValueChange={setPreset}>
            <SelectTrigger className="w-44 h-8 rounded-none text-xs" data-testid="ts-preset-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PRESET_OPTIONS.map(function (p) { return <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>; })}
            </SelectContent>
          </Select>
          {preset === "custom" && (
            <React.Fragment>
              <Input type="date" value={startDate} onChange={function (e) { setStartDate(e.target.value); }}
                     className="w-36 h-8 rounded-none text-xs" data-testid="ts-start" />
              <span className="text-xs">→</span>
              <Input type="date" value={endDate} onChange={function (e) { setEndDate(e.target.value); }}
                     className="w-36 h-8 rounded-none text-xs" data-testid="ts-end" />
            </React.Fragment>
          )}
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Bucket</span>
          <Select value={bucket} onValueChange={setBucket}>
            <SelectTrigger className="w-32 h-8 rounded-none text-xs" data-testid="ts-bucket"><SelectValue /></SelectTrigger>
            <SelectContent>
              {BUCKET_OPTIONS.map(function (b) { return <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>; })}
            </SelectContent>
          </Select>
          <MultiSelect options={clients.map(function (c) { return { value: c.id, label: c.name }; })}
                       value={clientF} onChange={setClientF} placeholder="Client" allLabel="All clients" width="w-40" testid="ts-f-client" />
          <MultiSelect options={brands.map(function (b) { return { value: b.id, label: b.name }; })}
                       value={brandF} onChange={setBrandF} placeholder="Brand" allLabel="All brands" width="w-40" testid="ts-f-brand" />
          <MultiSelect options={projects.map(function (p) { return { value: p.id, label: p.name }; })}
                       value={projectF} onChange={setProjectF} placeholder="Project" allLabel="All projects" width="w-44" testid="ts-f-project" />
          <MultiSelect options={members.map(function (m) { return { value: m.user_id, label: m.name || m.email }; })}
                       value={assigneeF} onChange={setAssigneeF} placeholder="Assignee" allLabel="All assignees" width="w-44" testid="ts-f-assignee" />
          <Select value={billableF} onValueChange={setBillableF}>
            <SelectTrigger className="w-32 h-8 rounded-none text-xs" data-testid="ts-billable"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All time</SelectItem>
              <SelectItem value="yes">Billable only</SelectItem>
              <SelectItem value="no">Non-billable</SelectItem>
            </SelectContent>
          </Select>
          <Input placeholder="Search task/note…" value={search}
                 onChange={function (e) { setSearch(e.target.value); }}
                 className="w-48 h-8 rounded-none text-xs" data-testid="ts-search" />
          <Button size="sm" className={"rounded-none h-8 " + (dirty ? "ring-2 ring-amber-400 animate-pulse" : "")}
                  onClick={run} disabled={loading} data-testid="ts-run">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Filter className="w-3.5 h-3.5 mr-1.5" />}
            Apply
            {dirty && !loading && <span className="ml-1 text-[10px] font-mono" data-testid="ts-run-dirty">•</span>}
          </Button>
        </div>

        <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-border/60">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Group by</span>
          <div className="flex items-center gap-1 flex-wrap" data-testid="ts-pivot-chain">
            {pivots.map(function (p, idx) {
              const opt = PIVOT_OPTIONS.find(function (o) { return o.value === p; });
              const isDragOver = dragOverIdx === idx;
              return (
                <div key={p}
                     draggable
                     onDragStart={function () { onPivotDragStart(idx); }}
                     onDragOver={function (e) { onPivotDragOver(idx, e); }}
                     onDrop={function () { onPivotDrop(idx); }}
                     onDragEnd={onPivotDragEnd}
                     className={"inline-flex items-center gap-1 border px-1.5 py-0.5 text-[11px] cursor-grab active:cursor-grabbing select-none "
                         + (isDragOver ? "bg-primary/30 border-primary" : "bg-primary/10 border-primary/30")}
                     data-testid={"ts-pivot-" + p}
                     title="Drag to reorder">
                  <GripVertical className="w-3 h-3 text-muted-foreground" />
                  <span className="font-mono text-[9px] text-muted-foreground">{idx + 1}</span>
                  <span className="font-semibold text-primary">{(opt && opt.label) || p}</span>
                  <button onClick={function () { movePivot(idx, -1); }} disabled={idx === 0}
                          className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                          title="Move up"
                          data-testid={"ts-pivot-up-" + p}>↑</button>
                  <button onClick={function () { movePivot(idx, 1); }} disabled={idx === pivots.length - 1}
                          className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                          title="Move down"
                          data-testid={"ts-pivot-down-" + p}>↓</button>
                  <button onClick={function () { dropPivot(idx); }}
                          className="text-rose-600 hover:text-rose-800" title="Remove"
                          data-testid={"ts-pivot-remove-" + p}>
                    <X className="w-3 h-3" />
                  </button>
                </div>
              );
            })}
            {pivots.length < 4 && (
              <Select onValueChange={addPivot} value="">
                <SelectTrigger className="w-32 h-6 rounded-none text-[11px]" data-testid="ts-pivot-add">
                  <SelectValue placeholder="+ Add level" />
                </SelectTrigger>
                <SelectContent>
                  {PIVOT_OPTIONS.filter(function (o) { return pivots.indexOf(o.value) < 0; }).map(function (o) {
                    return <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            )}
          </div>
          <span className="text-[10px] text-muted-foreground italic">Drag to reorder · use ↑↓ arrows or ✕ to remove · max 4 levels</span>
          <span className="ml-auto text-[10px] font-mono text-primary" data-testid="ts-pivot-current-order">
            Active: {pivots.map(function (p) { const o = PIVOT_OPTIONS.find(function (x) { return x.value === p; }); return (o && o.label) || p; }).join(" › ") || "—"}
          </span>
        </div>
      </div>

      {loading && !data && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {data && (
        <div className="space-y-3">
          <CollapsibleBlock blockKey="summary" title="Summary" defaultOpen>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="ts-summary">
              <SummaryCard label="Total hours" value={fmtH(data.tree.hours_total)} sub={data.entry_count + " entries"} />
              <SummaryCard label="Billable" value={fmtH(data.tree.hours_billable)} tone="emerald"
                           sub={data.tree.hours_total > 0 ? Math.round(data.tree.hours_billable / data.tree.hours_total * 100) + "% of total" : "0%"} />
              <SummaryCard label="Non-billable" value={fmtH(data.tree.hours_non_billable)} tone="amber" />
              <SummaryCard label="Working days" value={data.range.working_days} sub="in range" />
              <SummaryCard label="vs previous"
                           value={data.compare.delta_pct !== null ? ((data.compare.delta_pct > 0 ? "+" : "") + data.compare.delta_pct + "%") : "—"}
                           icon={data.compare.delta_pct > 0 ? "up" : (data.compare.delta_pct < 0 ? "down" : null)}
                           sub={fmtH(data.compare.prev_total_hours) + " prev"}
                           tone={data.compare.delta_pct > 0 ? "emerald" : (data.compare.delta_pct < 0 ? "rose" : "muted")} />
            </div>
          </CollapsibleBlock>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <CollapsibleBlock blockKey="utilization" title="Utilization" subtitle={`min ${data.settings.min_hours_per_day}h/day`}>
              <UtilizationTableInner data={data} />
            </CollapsibleBlock>
            <CollapsibleBlock blockKey="exceptions" title="Exceptions" subtitle={`Overtime >${data.settings.over_hours_threshold}h · deficit < min h`}>
              <ExceptionsPanelInner data={data} />
            </CollapsibleBlock>
          </div>

          <CollapsibleBlock blockKey="data_table" title="Data table"
                            subtitle={pivots.map((p) => { const o = PIVOT_OPTIONS.find((x) => x.value === p); return (o && o.label) || p; }).join(" › ")}
                            actions={
                              <label className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted-foreground cursor-pointer hover:text-foreground select-none"
                                     data-testid="ts-compare-toggle-label">
                                <input type="checkbox"
                                       className="w-3 h-3"
                                       checked={compareMode}
                                       onChange={(e) => setCompareMode(e.target.checked)}
                                       data-testid="ts-compare-toggle" />
                                vs previous
                              </label>
                            }>
            <PivotGrid data={data} pivots={pivots} expanded={expanded} toggleNode={toggleNode}
                        bucketCols={bucketCols} gridCols={gridCols} minGridWidth={minGridWidth}
                        onExpandAll={expandAll} onCollapseAll={collapseAll}
                        onExportXlsx={exportXlsx}
                        compareMode={compareMode}
                        selectedRowId={selectedRowId}
                        onSelectRow={toggleRowSelection}
                        onOpenTask={openTaskById}
                        onOpenProject={openProjectById}
                        onOpenAssignee={openAssigneeById}
                        onOpenProjectStatus={openProjectStatusById}
                        fullscreen={fullscreen} setFullscreen={setFullscreen} />
          </CollapsibleBlock>

          <CollapsibleBlock blockKey="charts" title="Charts" defaultOpen={false}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              <ChartCard title="Daily hours by assignee">
                <StackedBarChart chart={data.charts.stacked_bar} />
              </ChartCard>
              <ChartCard title="Billable vs non-billable">
                <BillablePie pie={data.charts.pie} />
              </ChartCard>
              <ChartCard title={"Hours trend · " + data.charts.trend.window + "-day rolling"}>
                <TrendLine trend={data.charts.trend} />
              </ChartCard>
            </div>
          </CollapsibleBlock>

          <CollapsibleBlock blockKey="sankey" title="Time flow · Sankey"
                            subtitle="Client → Project → Assignee"
                            defaultOpen={false}>
            <SankeyChart sankey={data.charts.sankey}
                          onSelect={function (kind, name) {
                            if (kind === "C") {
                              const c = clients.find(function (x) { return x.name === name; });
                              if (c) setClientF([c.id]);
                            } else if (kind === "P") {
                              const p = projects.find(function (x) { return x.name === name; });
                              if (p) setProjectF([p.id]);
                            } else if (kind === "A") {
                              const m = members.find(function (x) { return (x.name || x.email) === name; });
                              if (m) setAssigneeF([m.user_id]);
                            }
                          }} />
          </CollapsibleBlock>
        </div>
      )}

      {/* R268bbb · Feb 2026 — Drill-in popups */}
      {openTask && (
        <TaskDetail
          task={openTask}
          members={members}
          onClose={() => setOpenTask(null)}
          onPatch={async (patch) => {
            try {
              const { data } = await api.patch(`/tasks/${openTask.id}`, patch);
              setOpenTask(data);
            } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
          }} />
      )}
      {openProject && (
        <ProjectTasksPopup project={openProject} wsId={wsId}
                            onClose={() => setOpenProject(null)} />
      )}
      {openAssignee && (
        <EmployeeProfileDialog userId={openAssignee.user_id}
                                open={true}
                                onOpenChange={(o) => { if (!o) setOpenAssignee(null); }} />
      )}
      {openProjectStatus && (
        <Dialog open onOpenChange={(o) => { if (!o) setOpenProjectStatus(null); }}>
          <DialogContent className="w-[95vw] max-w-2xl rounded-none max-h-[92vh] overflow-y-auto"
                         data-testid="ts-project-status-dialog">
            <DialogHeader>
              <DialogTitle className="text-sm">Post update · {openProjectStatus.name}</DialogTitle>
            </DialogHeader>
            <ProjectStatusUpdatesTab
              projectId={openProjectStatus.id}
              project={{ id: openProjectStatus.id, name: openProjectStatus.name }}
              projectMembers={[]} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function SummaryCard(props) {
  const toneMap = {
    emerald: "border-emerald-500/30 bg-emerald-500/5",
    amber: "border-amber-500/30 bg-amber-500/5",
    rose: "border-rose-500/30 bg-rose-500/5",
    muted: "border-border bg-background",
  };
  const cls = toneMap[props.tone] || "border-border";
  return (
    <div className={"border " + cls + " px-3 py-2"}>
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1">
        {props.label}
        {props.icon === "up" && <TrendingUp className="w-3 h-3 text-emerald-600" />}
        {props.icon === "down" && <TrendingDown className="w-3 h-3 text-rose-600" />}
      </div>
      <div className="text-xl font-bold tracking-tight">{props.value}</div>
      {props.sub && <div className="text-[10px] text-muted-foreground">{props.sub}</div>}
    </div>
  );
}

function ChartCard(props) {
  return (
    <div className="border border-border">
      <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold bg-muted/40 border-b border-border">{props.title}</div>
      <div className="p-2">{props.children}</div>
    </div>
  );
}

function StackedBarChart(props) {
  const chart = props.chart;
  const users = chart.users.slice(0, 8);
  const rows = useMemo(function () {
    return chart.days.map(function (d, i) {
      const row = { day: d.slice(5) };
      users.forEach(function (u, j) {
        row[u] = (chart.matrix[i] && chart.matrix[i][j]) || 0;
      });
      return row;
    });
  }, [chart, users]);
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={rows}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey="day" tick={{ fontSize: 10 }} />
        <YAxis tick={{ fontSize: 10 }} />
        <Tooltip />
        <Legend wrapperStyle={{ fontSize: "10px" }} />
        {users.map(function (u, j) {
          return <Bar key={u} dataKey={u} stackId="a" fill={CHART_COLORS[j % CHART_COLORS.length]} />;
        })}
      </BarChart>
    </ResponsiveContainer>
  );
}

function BillablePie(props) {
  const rows = [
    { name: "Billable", value: props.pie.billable },
    { name: "Non-billable", value: props.pie.non_billable },
  ];
  return (
    <ResponsiveContainer width="100%" height={220}>
      <PieChart>
        <Pie data={rows} dataKey="value" nameKey="name" innerRadius={40} outerRadius={80}
             label={function (e) { return e.name + ": " + e.value + "h"; }}>
          <Cell fill="#059669" />
          <Cell fill="#F59E0B" />
        </Pie>
        <Tooltip />
      </PieChart>
    </ResponsiveContainer>
  );
}

function TrendLine(props) {
  const t = props.trend;
  const rows = useMemo(function () {
    return t.days.map(function (d, i) {
      return { day: d.slice(5), hours: t.hours[i], rolling: t.rolling[i] };
    });
  }, [t]);
  return (
    <ResponsiveContainer width="100%" height={220}>
      <LineChart data={rows}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey="day" tick={{ fontSize: 10 }} />
        <YAxis tick={{ fontSize: 10 }} />
        <Tooltip />
        <Legend wrapperStyle={{ fontSize: "10px" }} />
        <Line type="monotone" dataKey="hours" stroke="#4F46E5" strokeWidth={1} dot={{ r: 2 }} />
        <Line type="monotone" dataKey="rolling" stroke="#DC2626" strokeWidth={2} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

// R262 · Feb 2026 — Sankey renderer driven by the backend's
// nodes/links payload (Client → Project → Assignee time flow).
// Nodes carry a "C:", "P:" or "A:" prefix on their id so we can
// colour by layer. Links carry `value` = hours.
const SANKEY_LAYER_COLOR = { C: "#4F46E5", P: "#059669", A: "#DB2777" };

function SankeyNode(props) {
  const x = props.x, y = props.y, width = props.width, height = props.height;
  const p = props.payload || {};
  const id = p.id || "";
  const prefix = id.slice(0, 1);
  const color = SANKEY_LAYER_COLOR[prefix] || "#6B7280";
  const onSelect = props.onSelect;
  return (
    <Layer style={{ cursor: onSelect ? "pointer" : "default" }}
           onClick={function () { if (onSelect && prefix && p.name) onSelect(prefix, p.name); }}>
      <Rectangle x={x} y={y} width={width} height={height} fill={color} fillOpacity={0.85} />
      <text x={x + (width < 12 ? width + 4 : -4)} y={y + height / 2}
            textAnchor={width < 12 ? "start" : "end"}
            dominantBaseline="middle"
            style={{ fontSize: 10, fill: "#374151" }}>
        {p.name}
      </text>
    </Layer>
  );
}

function SankeyChart(props) {
  const s = props.sankey || { nodes: [], links: [] };
  // Recharts Sankey needs deep-cloned nodes/links so it can mutate.
  const chartData = useMemo(function () {
    return {
      nodes: s.nodes.map(function (n) { return { name: n.name, id: n.id }; }),
      links: s.links.map(function (l) { return { source: l.source, target: l.target, value: l.value }; }),
    };
  }, [s]);
  if (!s.nodes.length || !s.links.length) {
    return <div className="text-xs text-muted-foreground text-center py-8">No time-flow data in this range.</div>;
  }
  // R263 · click handlers — resolve the underlying node when the user
  // clicks a ribbon (link) or a rectangle (node) and forward the
  // (layer prefix, display name) tuple to the page so it can set the
  // corresponding filter chip (client / project / assignee).
  const onNodeClick = function (payload) {
    if (!props.onSelect) return;
    const p = (payload && payload.payload) || payload || {};
    const id = p.id || "";
    if (id.length < 2 || id[1] !== ":") return;
    props.onSelect(id[0], p.name);
  };
  const onLinkClick = function (payload) {
    if (!props.onSelect) return;
    const p = payload && payload.payload;
    if (!p) return;
    // Prefer the target (right-side node) which is the more-specific
    // slice; e.g. clicking a ribbon between Client → Project sets the
    // project filter.
    const t = (p.target && chartData.nodes[p.target.index !== undefined ? p.target.index : p.target]) || null;
    if (!t) return;
    const id = t.id || "";
    if (id.length < 2 || id[1] !== ":") return;
    props.onSelect(id[0], t.name);
  };
  return (
    <div className="w-full" style={{ height: Math.max(240, Math.min(560, chartData.nodes.length * 22)) }}
         data-testid="ts-chart-sankey">
      <div className="flex items-center gap-3 px-2 pb-1 text-[10px] text-muted-foreground">
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2" style={{ background: SANKEY_LAYER_COLOR.C }} /> Client</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2" style={{ background: SANKEY_LAYER_COLOR.P }} /> Project</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2" style={{ background: SANKEY_LAYER_COLOR.A }} /> Assignee</span>
        <span className="ml-3 italic">Click a node or ribbon to filter →</span>
        <span className="ml-auto italic">Ribbon width = hours logged</span>
      </div>
      <ResponsiveContainer width="100%" height="100%">
        <Sankey
          data={chartData}
          nodePadding={12}
          nodeWidth={10}
          linkCurvature={0.5}
          iterations={32}
          margin={{ left: 90, right: 120, top: 8, bottom: 8 }}
          node={<SankeyNode onSelect={props.onSelect} />}
          link={{ stroke: "#4F46E5", strokeOpacity: 0.15, fill: "#4F46E5", fillOpacity: 0.25 }}
          onClick={onLinkClick}>
          <Tooltip formatter={function (v) { return v.toFixed(1) + "h"; }} />
        </Sankey>
      </ResponsiveContainer>
    </div>
  );
}

function UtilizationTable(props) {
  const data = props.data;
  return (
    <div className="border border-border" data-testid="ts-util">
      <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold bg-muted/40 border-b border-border">
        Utilization (min {data.settings.min_hours_per_day}h/day)
      </div>
      <UtilizationTableInner data={data} />
    </div>
  );
}

function UtilizationTableInner(props) {
  const data = props.data;
  return (
    <div className="max-h-56 overflow-auto" data-testid="ts-util-inner">
      <table className="w-full text-xs">
        <thead className="text-[10px] uppercase tracking-widest text-muted-foreground bg-muted/20">
          <tr>
            <th className="text-left px-3 py-1">User</th>
            <th className="text-right px-2">Logged</th>
            <th className="text-right px-2">Capacity</th>
            <th className="text-right px-2">Util</th>
            <th className="text-right px-3">Billable</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {data.utilization.map(function (u) {
            const utl = u.utilization_pct || 0;
            const cls = utl >= 100 ? "text-emerald-700" : utl >= 75 ? "text-blue-700" : utl >= 50 ? "text-amber-700" : "text-rose-700";
            return (
              <tr key={u.user_id} data-testid={"ts-util-" + u.user_id}>
                <td className="px-3 py-1 truncate max-w-[160px]">{u.user_name}</td>
                <td className="px-2 text-right font-mono">{u.hours_total}h</td>
                <td className="px-2 text-right font-mono text-muted-foreground">{u.capacity}h</td>
                <td className={"px-2 text-right font-mono font-semibold " + cls}>{u.utilization_pct === null ? "—" : (u.utilization_pct + "%")}</td>
                <td className="px-3 text-right font-mono text-emerald-700">{u.billable_pct}%</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ExceptionsPanel(props) {
  const data = props.data;
  return (
    <div className="border border-border" data-testid="ts-exceptions">
      <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold bg-muted/40 border-b border-border flex items-center justify-between">
        <span>Exceptions</span>
        <span className="text-[10px] normal-case tracking-normal text-muted-foreground">
          Overtime &gt;{data.settings.over_hours_threshold}h/day · deficit &lt; min h
        </span>
      </div>
      <ExceptionsPanelInner data={data} />
    </div>
  );
}

function ExceptionsPanelInner(props) {
  const data = props.data;
  return (
    <div className="max-h-56 overflow-auto divide-y divide-border" data-testid="ts-exceptions-inner">
      {data.exceptions.length === 0 && (
        <div className="text-xs text-muted-foreground px-3 py-4">No exceptions in this range 🎉</div>
      )}
      {data.exceptions.map(function (x, i) {
        const bg = x.kind === "overtime" ? "bg-rose-50 dark:bg-rose-500/10" : "bg-amber-50 dark:bg-amber-500/10";
        const iconCls = x.kind === "overtime" ? "text-rose-600" : "text-amber-600";
        return (
          <div key={i} className={"px-3 py-1.5 text-xs flex items-center gap-2 " + bg} data-testid={"ts-exc-" + i}>
            <AlertTriangle className={"w-3.5 h-3.5 shrink-0 " + iconCls} />
            <div className="min-w-0 flex-1">
              <div className="font-semibold">{x.user_name}</div>
              {x.kind === "overtime" ? (
                <div className="text-[10px] text-muted-foreground">
                  {x.date} · <span className="text-rose-700 font-mono font-semibold">{x.hours}h</span> (over {x.threshold}h)
                </div>
              ) : (
                <div className="text-[10px] text-muted-foreground">
                  Deficit on <span className="font-mono font-semibold">{x.day_count}</span> day{x.day_count === 1 ? "" : "s"} · min {x.min_hours}h — e.g. {x.days.slice(0, 3).map(function (d) { return d.date + " (" + d.hours + "h)"; }).join(", ")}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// R268eee · Feb 2026 — Inline sparkline for the Δ % cell.
// Renders the last-4-periods + current-period hours as a tiny SVG
// polyline so the user sees momentum, not just a single number.
function Sparkline({ series }) {
  if (!Array.isArray(series) || series.length < 2) return null;
  const w = 60, h = 18, pad = 2;
  const max = Math.max(...series, 0.01);
  const min = Math.min(...series);
  const dx = (w - pad * 2) / (series.length - 1);
  const range = max - min || 1;
  const pts = series.map((v, i) => {
    const x = pad + i * dx;
    const y = h - pad - ((v - min) / range) * (h - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const last = series[series.length - 1];
  const first = series[0];
  const strokeCls = last > first ? "stroke-emerald-600"
                : last < first ? "stroke-rose-600"
                : "stroke-slate-400";
  return (
    <svg width={w} height={h} className="shrink-0" data-testid="ts-sparkline"
         style={{ overflow: "visible" }}>
      <polyline points={pts} fill="none" strokeWidth="1.4"
                className={strokeCls} strokeLinejoin="round" strokeLinecap="round" />
      {series.map((v, i) => {
        const x = pad + i * dx;
        const y = h - pad - ((v - min) / range) * (h - pad * 2);
        const dotCls = i === series.length - 1
          ? (last > first ? "fill-emerald-600" : last < first ? "fill-rose-600" : "fill-slate-400")
          : "fill-slate-300";
        return <circle key={i} cx={x} cy={y} r={i === series.length - 1 ? 1.8 : 1} className={dotCls} />;
      })}
    </svg>
  );
}


function PivotGrid(props) {
  const data = props.data;
  const bucketCols = props.bucketCols;
  const pivots = props.pivots;
  const fullscreen = props.fullscreen;
  // R268f · Feb 2026 — grid geometry (must match parent's gridCols).
  // Widened bucket cols to 84 px so YYYY-MM-DD header labels don't clip.
  const bucketColWidth = 84;
  const labelColWidth = 300;
  const metricColWidth = 92;
  const entriesColWidth = 72;
  const compareMode = !!props.compareMode;
  // R268zz · Feb 2026 — When compare-period is ON we insert two extra
  // 92px columns (Prev H · Δ %) after the entries count column but
  // before the day-bucket grid. Keeping the width bookkeeping local
  // means every downstream cell keeps aligning with the sticky header.
  const compareCols = compareMode ? [
    { label: "Prev H",  key: "prev_hours_total", numeric: true },
    { label: "Δ %",     key: "delta_pct",         numeric: true, width: 136 },
  ] : [];
  const compareColWidth = 92;
  const gridCols = `${labelColWidth}px ${metricColWidth}px ${metricColWidth}px ${metricColWidth}px ${entriesColWidth}px `
    + compareCols.map((cc) => `${cc.width || compareColWidth}px`).join(" ")
    + (compareCols.length ? " " : "")
    + bucketCols.map(() => `${bucketColWidth}px`).join(" ");
  const fixedColsWidth = labelColWidth + metricColWidth * 3 + entriesColWidth
    + compareCols.reduce((s, cc) => s + (cc.width || compareColWidth), 0)
    + (4 + compareCols.length) * 4;
  const minGridWidth = fixedColsWidth + bucketCols.length * (bucketColWidth + 4);

  const rows = [];
  // R268ddd · Feb 2026 — Column-level sorting. Sort scope is per-parent
  // so the grouping hierarchy is preserved: children are re-ordered
  // inside their own parent, never flattened across the tree. Header
  // clicks cycle asc → desc → unsorted (indicator shown next to label).
  const [sort, setSort] = React.useState({ key: null, dir: null });
  const handleSort = (key) => {
    setSort((prev) => {
      if (prev.key !== key) return { key: key, dir: "asc" };
      if (prev.dir === "asc") return { key: key, dir: "desc" };
      if (prev.dir === "desc") return { key: null, dir: null };
      return { key: key, dir: "asc" };
    });
  };
  const getSortValue = (n, key) => {
    if (!key) return null;
    if (key === "label") return (n.label || "").toString().toLowerCase();
    if (key.indexOf("bucket:") === 0) {
      const b = key.slice(7);
      const v = n.buckets && n.buckets[b];
      return v === undefined || v === null ? null : Number(v);
    }
    const v = n[key];
    return v === undefined || v === null ? null : Number(v);
  };
  const compareNodes = (a, b) => {
    const av = getSortValue(a, sort.key);
    const bv = getSortValue(b, sort.key);
    const aNull = av === null || av === undefined || (typeof av === "number" && isNaN(av));
    const bNull = bv === null || bv === undefined || (typeof bv === "number" && isNaN(bv));
    if (aNull && bNull) return 0;
    if (aNull) return 1; // nulls always to bottom
    if (bNull) return -1;
    let cmp;
    if (typeof av === "string" || typeof bv === "string") {
      cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" });
    } else {
      cmp = av < bv ? -1 : av > bv ? 1 : 0;
    }
    return sort.dir === "asc" ? cmp : -cmp;
  };
  // R268yy · Feb 2026 — Track seen ids so the tree walk is *bulletproof*
  // against any upstream mutation that could produce duplicate children
  // (React re-renders / stale in-flight responses / third-party effect
  // hooks that touch the response object). Even if `data.tree` somehow
  // has the same node listed twice, the visible grid stays clean.
  const _seen = new Set();
  // R268fff · Feb 2026 — Track the currently-active assignee user_id
  // as we descend. When a node is a pivot=assignee row, its `key`
  // becomes the active user id, inherited by every descendant. Rows
  // outside any assignee subtree carry `activeUserId = null` and get
  // no short-day highlighting (threshold is undefined at the group
  // level with multiple assignees).
  function walk(node, depth, activeUserId) {
    let kids = (node.children || []).slice();
    if (sort.key && sort.dir) kids.sort(compareNodes);
    kids.forEach(function (c) {
      if (!c || _seen.has(c.id)) return;
      _seen.add(c.id);
      const isOpen = props.expanded.has(c.id);
      const hasKids = (c.children || []).length > 0;
      const nextUser = c.pivot_key === "assignee"
        ? (c.key && c.key !== "__none__" ? c.key : null)
        : activeUserId;
      rows.push({ id: c.id, node: c, depth: depth, isOpen: isOpen,
                    hasKids: hasKids, activeUserId: nextUser });
      if (isOpen && hasKids) walk(c, depth + 1, nextUser);
    });
  }
  walk(data.tree, 0, null);

  // R268fff · Feb 2026 — Effective min-hours-per-day for a given user.
  // Falls back to the workspace default when no per-user override.
  const perUserMin = (data.settings && data.settings.per_user_min_hours) || {};
  const wsMinPerDay = Number(data.settings && data.settings.min_hours_per_day) || 0;
  const bucketWdays = (data.settings && data.settings.bucket_wdays) || {};
  const _minFor = (uid) => {
    if (!uid) return null;
    const override = perUserMin[uid];
    if (override !== undefined && override !== null) return Number(override) || 0;
    return wsMinPerDay;
  };
  // Expected hours for a given bucket key under a given user. Returns
  // 0 (=> no highlighting) for buckets outside {day, week, month,
  // quarter} or when the threshold is disabled (0).
  const _expectedForBucket = (uid, bkey) => {
    const min = _minFor(uid);
    if (!min || min <= 0) return 0;
    const wd = bucketWdays[bkey];
    if (wd === undefined || wd === null) return 0;
    return Number(wd) * min;
  };

  // Sort indicator icon for a header. Neutral = tiny up-down (hover hint),
  // active-asc = up arrow, active-desc = down arrow.
  const SortIcon = ({ colKey }) => {
    const active = sort.key === colKey;
    if (!active) {
      return <ArrowUpDown className="w-3 h-3 opacity-30 group-hover/hdr:opacity-70 shrink-0" />;
    }
    return sort.dir === "asc"
      ? <ArrowUp className="w-3 h-3 text-primary shrink-0" data-testid={"ts-sort-asc-" + colKey} />
      : <ArrowDown className="w-3 h-3 text-primary shrink-0" data-testid={"ts-sort-desc-" + colKey} />;
  };

  const wrapperCls = fullscreen
    ? "fixed inset-0 z-[120] bg-background flex flex-col p-4"
    : "border border-border";

  // R268vv/R268aaa/R268bbb · Feb 2026 — Depth-based colour banding
  // with a subtle sky-blue ramp. Level-0 (top parent) is the darkest
  // fill; each deeper level fades toward the paper white so the
  // hierarchy reads at a glance even without indentation. Applied via
  // explicit HSL fills (not opacity on --muted) so the ramp is
  // visible in both light and dark themes.
  const DEPTH_BG_LIGHT = [
    "hsl(210, 55%, 88%)",   // depth 0 — darkest sky blue
    "hsl(210, 60%, 93%)",   // depth 1
    "hsl(210, 70%, 96%)",   // depth 2
    "hsl(210, 80%, 98%)",   // depth 3
    "hsl(210, 100%, 99.5%)",// depth 4+ (leaves — near white)
  ];
  const DEPTH_BG_DARK = [
    "hsl(210, 30%, 26%)",   // depth 0 — darkest
    "hsl(210, 28%, 22%)",   // depth 1
    "hsl(210, 26%, 18%)",   // depth 2
    "hsl(210, 24%, 14%)",   // depth 3
    "hsl(210, 22%, 11%)",   // depth 4+
  ];
  const isDark = typeof document !== "undefined"
    && document.documentElement.classList.contains("dark");
  const DEPTH_BG = isDark ? DEPTH_BG_DARK : DEPTH_BG_LIGHT;
  const _depthBg = (d, hasKids) => {
    if (!hasKids) return DEPTH_BG[Math.min(d + 1, DEPTH_BG.length - 1)];
    return DEPTH_BG[Math.min(d, DEPTH_BG.length - 1)];
  };
  const _depthFontWeight = (d, hasKids) => {
    if (!hasKids) return "";
    if (d === 0) return " font-bold";
    if (d === 1) return " font-semibold";
    return " font-medium";
  };
  // Sticky-left keeps whatever bg the row uses so day-column numbers
  // never bleed through when the grid is horizontally scrolled.
  const stickyBgFor = (depth, hasKids) => _depthBg(depth, hasKids);

  return (
    <div className={wrapperCls} data-testid="ts-grid-wrap">
      <div className="flex items-center gap-2 px-2 py-1.5 bg-muted/40 border-b border-border">
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
          {pivots.map(function (p) { const opt = PIVOT_OPTIONS.find(function (o) { return o.value === p; }); return (opt && opt.label) || p; }).join(" › ")}
        </span>
        <span className="text-[10px] text-muted-foreground">· {rows.length} rows · {bucketCols.length} {data.bucket} cols{sort.key && sort.dir ? " · sorted " + sort.dir : ""}</span>
        <div className="ml-auto flex items-center gap-1">
          <Button size="sm" variant="outline" className="h-6 rounded-none text-[10px] px-2"
                  onClick={props.onExpandAll} data-testid="ts-grid-expand-all">Expand all</Button>
          <Button size="sm" variant="outline" className="h-6 rounded-none text-[10px] px-2"
                  onClick={props.onCollapseAll} data-testid="ts-grid-collapse-all">Collapse all</Button>
          {props.onExportXlsx && (
            <Button size="sm" variant="outline" className="h-6 rounded-none text-[10px] px-2"
                    onClick={props.onExportXlsx} data-testid="ts-grid-export-xlsx">
              <Download className="w-3 h-3 mr-1" /> Excel
            </Button>
          )}
          <Button size="sm" variant="outline" className="h-6 rounded-none text-[10px] px-2"
                  onClick={function () { props.setFullscreen(!fullscreen); }}
                  data-testid="ts-grid-fullscreen">
            {fullscreen ? <React.Fragment><Minimize2 className="w-3 h-3 mr-1" />Exit</React.Fragment>
                        : <React.Fragment><Maximize2 className="w-3 h-3 mr-1" />Fullscreen</React.Fragment>}
          </Button>
        </div>
      </div>
      <div className={"overflow-auto " + (fullscreen ? "flex-1" : "max-h-[70vh]")} data-testid="ts-grid">
        {/* Header row — sticky top, sticky-left first cell is opaque and z-40 (above data sticky cells) */}
        <div className="grid text-[9px] uppercase tracking-wider text-muted-foreground font-semibold sticky top-0 z-30 border-b-2 border-border bg-muted whitespace-nowrap"
             style={{ gridTemplateColumns: gridCols, minWidth: minGridWidth + "px", gap: "4px", padding: "6px 0" }}>
          <div className="sticky left-0 z-40 flex items-center pl-3 pr-2 border-r border-border bg-muted group/hdr cursor-pointer select-none hover:text-primary"
               style={{ backgroundColor: "hsl(var(--muted))" }}
               onClick={() => handleSort("label")}
               role="button"
               title="Sort by label"
               data-testid="ts-header-label">
            <span className="flex-1 min-w-0 truncate">
              {pivots.map(function (p) { const opt = PIVOT_OPTIONS.find(function (o) { return o.value === p; }); return (opt && opt.label) || p; }).join(" › ")}
            </span>
            <SortIcon colKey="label" />
          </div>
          <div className="text-right bg-muted/60 border-r border-slate-400/70 pr-2 flex items-center justify-end gap-1 group/hdr cursor-pointer select-none hover:text-primary"
               onClick={() => handleSort("hours_total")}
               role="button" title="Sort by total hours"
               data-testid="ts-header-total">
            <SortIcon colKey="hours_total" />Total h
          </div>
          <div className="text-right bg-muted/60 text-emerald-700 border-r border-slate-400/70 pr-2 flex items-center justify-end gap-1 group/hdr cursor-pointer select-none hover:text-primary"
               onClick={() => handleSort("hours_billable")}
               role="button" title="Sort by billable hours"
               data-testid="ts-header-billable">
            <SortIcon colKey="hours_billable" />Billable h
          </div>
          <div className="text-right bg-muted/60 text-amber-700 border-r border-slate-400/70 pr-2 flex items-center justify-end gap-1 group/hdr cursor-pointer select-none hover:text-primary"
               onClick={() => handleSort("hours_non_billable")}
               role="button" title="Sort by non-billable hours"
               data-testid="ts-header-nonbill">
            <SortIcon colKey="hours_non_billable" />Non-bill h
          </div>
          <div className="text-right bg-muted/60 border-r border-slate-400/70 pr-2 flex items-center justify-end gap-1 group/hdr cursor-pointer select-none hover:text-primary"
               onClick={() => handleSort("entry_count")}
               role="button" title="Sort by entry count"
               data-testid="ts-header-entries">
            <SortIcon colKey="entry_count" />Entries
          </div>
          {compareMode && (
            <React.Fragment>
              <div className="text-right bg-muted/60 border-r border-slate-400/70 pr-2 flex items-center justify-end gap-1 text-blue-700 group/hdr cursor-pointer select-none hover:text-primary"
                   onClick={() => handleSort("prev_hours_total")}
                   role="button" title="Sort by previous-period hours"
                   data-testid="ts-header-prev">
                <SortIcon colKey="prev_hours_total" />Prev h
              </div>
              <div className="text-right bg-muted/60 border-r border-slate-400/70 pr-2 flex items-center justify-end gap-1 group/hdr cursor-pointer select-none hover:text-primary"
                   onClick={() => handleSort("delta_pct")}
                   role="button" title="Sort by delta %"
                   data-testid="ts-header-delta">
                <SortIcon colKey="delta_pct" />Δ %
              </div>
            </React.Fragment>
          )}
          {bucketCols.map(function (b, i) {
            const bk = "bucket:" + b;
            return (
              <div key={b}
                   className={"text-right bg-muted/60 font-mono normal-case tracking-normal flex items-center justify-end gap-1 pr-2 group/hdr cursor-pointer select-none hover:text-primary " + (i < bucketCols.length - 1 ? "border-r border-slate-400/70" : "")}
                   onClick={() => handleSort(bk)}
                   role="button"
                   title={"Sort by " + b}
                   data-testid={"ts-header-bucket-" + b}>
                <SortIcon colKey={bk} /><span title={b}>{b}</span>
              </div>
            );
          })}
        </div>
        {rows.length === 0 && (
          <div className="text-xs text-muted-foreground px-3 py-6" data-testid="ts-grid-empty">
            No data for the selected filters.
          </div>
        )}
        {rows.map(function (r) {
          const c = r.node;
          const rowFontCls = _depthFontWeight(r.depth, r.hasKids);
          const rowBgInline = _depthBg(r.depth, r.hasKids);
          const isSelected = props.selectedRowId === r.id;
          // R268ccc · Row selection ring — a persistent primary tint
          // + left indicator strip when the user clicks a row. Click
          // again to un-pin.
          const selectionStyle = isSelected
            ? { boxShadow: "inset 3px 0 0 0 hsl(var(--primary))",
                backgroundColor: "hsl(210, 80%, 82%)" }
            : { backgroundColor: rowBgInline };
          // Sticky-left is opaque with the row's exact bg so day cols
          // never bleed through when horizontally scrolled AND depth
          // banding still flows across the row. When the row is
          // selected, use the selection tint so the highlight extends
          // to the sticky column.
          const stickyInline = { backgroundColor: isSelected ? "hsl(210, 80%, 82%)" : rowBgInline };
          // Wire label clicks to the appropriate drill-in popup based
          // on this row's pivot key.
          const pk = c.pivot_key;
          const canOpen = pk === "task" || pk === "project" || pk === "assignee";
          const openLabel = (e) => {
            e && e.stopPropagation && e.stopPropagation();
            if (pk === "task") props.onOpenTask && props.onOpenTask(c.key);
            else if (pk === "project") props.onOpenProject && props.onOpenProject(c.key);
            else if (pk === "assignee") props.onOpenAssignee && props.onOpenAssignee(c.key);
          };
          const openStatus = (e) => {
            e && e.stopPropagation && e.stopPropagation();
            props.onOpenProjectStatus && props.onOpenProjectStatus(c.key);
          };
          return (
            <div key={r.id}
                 className={"grid py-1 text-xs items-center border-b border-slate-400/70 group cursor-pointer " + rowFontCls}
                 style={{ gridTemplateColumns: gridCols, minWidth: minGridWidth + "px", gap: "4px", ...selectionStyle }}
                 onClick={() => props.onSelectRow && props.onSelectRow(r.id)}
                 data-selected={isSelected ? "true" : undefined}
                 data-testid={"ts-row-" + r.id}>
              <div className={"flex items-center gap-1 min-w-0 sticky left-0 z-20 border-r border-slate-400/70 pr-2 group-hover:bg-primary/5"}
                   style={{ paddingLeft: (r.depth * 14 + 12) + "px", ...stickyInline }}>
                {r.hasKids ? (
                  <button onClick={function (e) { e.stopPropagation(); props.toggleNode(r.id); }}
                          className="hover:text-primary shrink-0 w-4 h-4 flex items-center justify-center"
                          data-testid={"ts-toggle-" + r.id}
                          aria-label={r.isOpen ? "Collapse" : "Expand"}>
                    {r.isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                  </button>
                ) : <span className="w-4 h-4 inline-block shrink-0" />}
                {pk === "project" && c.key && c.key !== "__none__" && (
                  <button onClick={openStatus}
                          className="shrink-0 w-4 h-4 flex items-center justify-center text-muted-foreground hover:text-primary"
                          data-testid={"ts-status-" + r.id}
                          aria-label="Post project status update"
                          title="Post project status update">
                    <MessageSquarePlus className="w-3 h-3" />
                  </button>
                )}
                {canOpen && c.key && c.key !== "__none__" ? (
                  <button onClick={openLabel}
                          className="truncate text-left hover:underline hover:text-primary min-w-0 flex-1"
                          data-testid={"ts-open-" + r.id}
                          title={c.label}>
                    {c.label}
                  </button>
                ) : (
                  <span className="truncate min-w-0 flex-1" title={c.label}>{c.label}</span>
                )}
              </div>
              <div className="text-right font-mono font-semibold border-r border-slate-400/60 pr-2 group-hover:bg-primary/5">{fmtH(c.hours_total)}</div>
              <div className="text-right font-mono text-emerald-700 border-r border-slate-400/60 pr-2 group-hover:bg-primary/5">{fmtH(c.hours_billable)}</div>
              <div className="text-right font-mono text-amber-700 border-r border-slate-400/60 pr-2 group-hover:bg-primary/5">{fmtH(c.hours_non_billable)}</div>
              <div className="text-right font-mono text-muted-foreground border-r border-slate-400/60 pr-2 group-hover:bg-primary/5">{c.entry_count}</div>
              {compareMode && (
                <React.Fragment>
                  <div className="text-right font-mono text-blue-700 border-r border-slate-400/60 pr-2 group-hover:bg-primary/5"
                       data-testid={"ts-row-prev-" + r.id}>
                    {c.prev_hours_total !== undefined && c.prev_hours_total !== null ? fmtH(c.prev_hours_total) : "—"}
                  </div>
                  <div className={"flex items-center gap-1 justify-end border-r border-slate-400/60 pr-2 group-hover:bg-primary/5 " +
                       (c.delta_pct === null || c.delta_pct === undefined
                         ? "text-muted-foreground"
                         : c.delta_pct > 0 ? "text-emerald-700"
                         : c.delta_pct < 0 ? "text-rose-700"
                         : "text-muted-foreground")}
                       data-testid={"ts-row-delta-" + r.id}>
                    <Sparkline series={c.trend_series} />
                    <span className="font-mono text-right">
                      {c.delta_pct === null || c.delta_pct === undefined ? "—"
                        : (c.delta_pct > 0 ? "+" : "") + c.delta_pct + "%"}
                    </span>
                  </div>
                </React.Fragment>
              )}
              {bucketCols.map(function (b, i) {
                const v = c.buckets[b];
                // R268fff · Feb 2026 — Short-period highlight. Yellow
                // background + red text when the cell's logged hours
                // fall below the effective threshold for this
                // employee/bucket combination. Skipped for:
                //   • non-assignee-scoped rows (no active user in path)
                //   • bucket types outside {day, week, month, quarter}
                //   • disabled thresholds (min = 0)
                //   • zero-value cells (empty day is not "short", it's off)
                const expected = _expectedForBucket(r.activeUserId, b);
                const isShort = expected > 0 && v > 0 && v < expected;
                const cellStyle = isShort
                  ? { backgroundColor: "hsl(48, 100%, 78%)", color: "hsl(0, 78%, 42%)", fontWeight: 700 }
                  : undefined;
                const shortTitle = isShort
                  ? `Short: ${v.toFixed(1)}h logged, min ${expected.toFixed(1)}h expected`
                  : undefined;
                return (
                  <div key={b}
                       className={"text-right font-mono text-[10px] pr-2 group-hover:bg-primary/5 " + (v ? "text-foreground" : "text-muted-foreground/40") + " " + (i < bucketCols.length - 1 ? "border-r border-slate-400/60" : "")}
                       style={cellStyle}
                       title={shortTitle}
                       data-short={isShort ? "true" : undefined}
                       data-testid={isShort ? ("ts-cell-short-" + r.id + "-" + b) : undefined}>
                    {v ? v.toFixed(1) : "·"}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
